<template>
  <SignupStepFrame
    :step-number="1"
    :step-total="5"
    eyebrow="Step 1 — Your workspace"
    :title="titleHtml"
    subtitle="These details power your workspace. You can change everything later in Settings."
    :can-go-next="isValid"
    @next="onNext"
    @back="$emit('back')"
    @saveAndExit="$emit('saveAndExit')"
  >
    <form class="wiz-company" novalidate @submit.prevent="onNext">

      <!-- Identity ------------------------------------------------------ -->
      <div class="wiz-company__section">
        <SignupTextField
          label="Company name"
          :value="form.name"
          placeholder="Your team's name"
          autocomplete="organization"
          required
          @input="(v) => (form.name = v)"
          @blur="touched.name = true"
          :error="touched.name && !nameValid ? 'Please enter your company name.' : ''"
        />

        <div class="wiz-company__row">
          <SignupSelect
            label="Country"
            :value="form.country"
            :options="countryOptions"
            placeholder="Search countries…"
            required
            filterable
            @input="onCountryChange"
            :error="touched.country && !countryValid ? 'Please pick a country.' : ''"
          />
          <SignupSelect
            label="Time zone"
            :value="form.timezone"
            :options="timezoneOptions"
            placeholder="Select a time zone"
            required
            :hint="timezoneHint"
            @input="onTimezoneChange"
            :error="touched.timezone && !timezoneValid ? 'Please pick a time zone.' : ''"
          />
        </div>
      </div>

      <!-- Feedback cadence --------------------------------------------- -->
      <div class="wiz-company__section">
        <div class="wiz-company__section-title">
          Feedback cadence
          <span class="wiz-company__required" aria-hidden="true">*</span>
        </div>
        <div class="wiz-company__section-hint">
          Questions are delivered daily. This sets how many questions per day and how often open-ended prompts appear. You can change it any time.
        </div>
        <IntensityCadencePicker
          :value="form.cadence"
          :summaryFor="cadenceSummaryFor"
          aria-label="Feedback cadence"
          @input="onCadenceSelect"
        />
      </div>

      <!-- Delivery time ------------------------------------------------- -->
      <div class="wiz-company__section">
        <div class="wiz-company__time-field">
          <SignupSelect
            label="Delivery time"
            :value="form.delivery_time"
            :options="deliveryTimeOptions"
            placeholder="Select a time"
            required
            @input="(v) => { form.delivery_time = v; touched.delivery_time = true }"
            :error="touched.delivery_time && !deliveryTimeValid ? 'Please pick a delivery time.' : ''"
          />
          <span class="wiz-company__time-help">When daily questions go out in your local time zone.</span>
        </div>
      </div>

    </form>
  </SignupStepFrame>
</template>

<script>
import SignupStepFrame from './SignupStepFrame.vue'
import SignupTextField from './_primitives/SignupTextField.vue'
import SignupSelect from './_primitives/SignupSelect.vue'
import IntensityCadencePicker from '@/components/intensity/IntensityCadencePicker.vue'
import { CADENCE_TARGETS } from '@/store/intensity-mapping'

/**
 * WizardCompanyStep — Step 1 of the self-serve signup wizard.
 * Collects company name, country, timezone, feedback cadence preset,
 * and the daily question delivery time. The legacy HQ "location" field
 * was dropped in Phase D Track D2 along with the rest of that section.
 *
 * Contract with SignupWizard:
 *  - Wraps <SignupStepFrame> and re-uses its Continue/Back/Save&Exit UI.
 *  - Emits `next` when the user confirms Step 1 — the parent wizard
 *    advances to Step 2. Commits local form state to Vuex
 *    (`signup/UPDATE_COMPANY`) BEFORE emitting so the next step reads
 *    the fresh values.
 *  - Also re-emits `back` and `saveAndExit` verbatim.
 *
 * Hydration:
 *  - Reads $store.state.signup.wizardData.company on created() so a
 *    refresh mid-wizard restores what the user typed. The signup module
 *    already persists its state to sessionStorage, so we only need to
 *    pull the values onto our local `form`.
 */

const COUNTRY_OPTIONS = [
  { value: 'Thailand', label: 'Thailand' },
  { value: 'United States', label: 'United States' },
  { value: 'United Kingdom', label: 'United Kingdom' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'Philippines', label: 'Philippines' },
  { value: 'India', label: 'India' },
  { value: 'Australia', label: 'Australia' },
  { value: 'Indonesia', label: 'Indonesia' },
  { value: 'Vietnam', label: 'Vietnam' },
  { value: 'Malaysia', label: 'Malaysia' },
  { value: 'Japan', label: 'Japan' },
  { value: 'South Korea', label: 'South Korea' },
  { value: 'China', label: 'China' },
  { value: 'Hong Kong', label: 'Hong Kong' },
  { value: 'Taiwan', label: 'Taiwan' },
  { value: 'Germany', label: 'Germany' },
  { value: 'France', label: 'France' },
  { value: 'Spain', label: 'Spain' },
  { value: 'Italy', label: 'Italy' },
  { value: 'Netherlands', label: 'Netherlands' },
  { value: 'Sweden', label: 'Sweden' },
  { value: 'Norway', label: 'Norway' },
  { value: 'Denmark', label: 'Denmark' },
  { value: 'Finland', label: 'Finland' },
  { value: 'Switzerland', label: 'Switzerland' },
  { value: 'Ireland', label: 'Ireland' },
  { value: 'Portugal', label: 'Portugal' },
  { value: 'Belgium', label: 'Belgium' },
  { value: 'Austria', label: 'Austria' },
  { value: 'Poland', label: 'Poland' },
  { value: 'Canada', label: 'Canada' },
  { value: 'Mexico', label: 'Mexico' },
  { value: 'Brazil', label: 'Brazil' },
  { value: 'Argentina', label: 'Argentina' },
  { value: 'Chile', label: 'Chile' },
  { value: 'Colombia', label: 'Colombia' },
  { value: 'United Arab Emirates', label: 'United Arab Emirates' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia' },
  { value: 'Qatar', label: 'Qatar' },
  { value: 'Israel', label: 'Israel' },
  { value: 'Turkey', label: 'Turkey' },
  { value: 'Egypt', label: 'Egypt' },
  { value: 'South Africa', label: 'South Africa' },
  { value: 'Kenya', label: 'Kenya' },
  { value: 'Nigeria', label: 'Nigeria' },
  { value: 'New Zealand', label: 'New Zealand' },
]

/**
 * Country → primary timezone offset (integer hours from UTC).
 *
 * For multi-zone countries (US, Canada, Australia, Brazil, etc.) we pick
 * the most-populous business hub as the default. The user can always
 * adjust the timezone manually if their HQ sits in a different zone, and
 * once they do we stop auto-overriding it on country change.
 *
 * Half-hour zones (India +5:30, Iran +3:30) are rounded to the nearest
 * whole hour to fit the integer-only timezone select. Close enough for a
 * daily-question delivery default — the user nudges if it matters.
 */
const COUNTRY_TIMEZONE = {
  'Thailand': 7,
  'United States': -5,         // Eastern (NYC, DC) — largest by population
  'United Kingdom': 0,
  'Singapore': 8,
  'Philippines': 8,
  'India': 6,                  // IST is +5:30 → rounded to +6
  'Australia': 10,             // AEST (Sydney, Melbourne) — largest hub
  'Indonesia': 7,              // WIB (Jakarta)
  'Vietnam': 7,
  'Malaysia': 8,
  'Japan': 9,
  'South Korea': 9,
  'China': 8,
  'Hong Kong': 8,
  'Taiwan': 8,
  'Germany': 1,
  'France': 1,
  'Spain': 1,
  'Italy': 1,
  'Netherlands': 1,
  'Sweden': 1,
  'Norway': 1,
  'Denmark': 1,
  'Finland': 2,
  'Switzerland': 1,
  'Ireland': 0,
  'Portugal': 0,
  'Belgium': 1,
  'Austria': 1,
  'Poland': 1,
  'Canada': -5,                // Eastern (Toronto, Ottawa)
  'Mexico': -6,                // Central (Mexico City)
  'Brazil': -3,                // BRT (São Paulo)
  'Argentina': -3,
  'Chile': -4,
  'Colombia': -5,
  'United Arab Emirates': 4,
  'Saudi Arabia': 3,
  'Qatar': 3,
  'Israel': 2,
  'Turkey': 3,
  'Egypt': 2,
  'South Africa': 2,
  'Kenya': 3,
  'Nigeria': 1,
  'New Zealand': 12,
}

/**
 * IANA timezone → country (one-to-many, but stored as zone-keyed for O(1)
 * lookup). The browser exposes the IANA zone via
 * `Intl.DateTimeFormat().resolvedOptions().timeZone`, so this is our most
 * reliable signal for "which country is this user in?"
 *
 * Argentina is the special case — its zones nest under `America/Argentina/*`
 * with many sub-zones, so we pattern-match those rather than enumerating.
 */
const TIMEZONE_TO_COUNTRY = {
  // Thailand
  'Asia/Bangkok': 'Thailand',
  // United States — every continental + AK/HI zone
  'America/New_York': 'United States',
  'America/Detroit': 'United States',
  'America/Kentucky/Louisville': 'United States',
  'America/Kentucky/Monticello': 'United States',
  'America/Indiana/Indianapolis': 'United States',
  'America/Indiana/Vincennes': 'United States',
  'America/Indiana/Winamac': 'United States',
  'America/Indiana/Marengo': 'United States',
  'America/Indiana/Petersburg': 'United States',
  'America/Indiana/Vevay': 'United States',
  'America/Indiana/Tell_City': 'United States',
  'America/Indiana/Knox': 'United States',
  'America/Chicago': 'United States',
  'America/Menominee': 'United States',
  'America/North_Dakota/Center': 'United States',
  'America/North_Dakota/New_Salem': 'United States',
  'America/North_Dakota/Beulah': 'United States',
  'America/Denver': 'United States',
  'America/Boise': 'United States',
  'America/Phoenix': 'United States',
  'America/Los_Angeles': 'United States',
  'America/Anchorage': 'United States',
  'America/Juneau': 'United States',
  'America/Sitka': 'United States',
  'America/Metlakatla': 'United States',
  'America/Yakutat': 'United States',
  'America/Nome': 'United States',
  'America/Adak': 'United States',
  'Pacific/Honolulu': 'United States',
  // United Kingdom
  'Europe/London': 'United Kingdom',
  // Singapore
  'Asia/Singapore': 'Singapore',
  // Philippines
  'Asia/Manila': 'Philippines',
  // India (legacy alias Calcutta still exists in some runtimes)
  'Asia/Kolkata': 'India',
  'Asia/Calcutta': 'India',
  // Australia
  'Australia/Sydney': 'Australia',
  'Australia/Melbourne': 'Australia',
  'Australia/Brisbane': 'Australia',
  'Australia/Adelaide': 'Australia',
  'Australia/Perth': 'Australia',
  'Australia/Hobart': 'Australia',
  'Australia/Darwin': 'Australia',
  'Australia/Lord_Howe': 'Australia',
  'Australia/Currie': 'Australia',
  'Australia/Lindeman': 'Australia',
  'Australia/Broken_Hill': 'Australia',
  'Australia/Eucla': 'Australia',
  // Indonesia
  'Asia/Jakarta': 'Indonesia',
  'Asia/Pontianak': 'Indonesia',
  'Asia/Makassar': 'Indonesia',
  'Asia/Jayapura': 'Indonesia',
  // Vietnam
  'Asia/Ho_Chi_Minh': 'Vietnam',
  'Asia/Saigon': 'Vietnam',
  // Malaysia
  'Asia/Kuala_Lumpur': 'Malaysia',
  'Asia/Kuching': 'Malaysia',
  // Japan
  'Asia/Tokyo': 'Japan',
  // South Korea
  'Asia/Seoul': 'South Korea',
  // China
  'Asia/Shanghai': 'China',
  'Asia/Urumqi': 'China',
  // Hong Kong
  'Asia/Hong_Kong': 'Hong Kong',
  // Taiwan
  'Asia/Taipei': 'Taiwan',
  // Germany
  'Europe/Berlin': 'Germany',
  'Europe/Busingen': 'Germany',
  // France
  'Europe/Paris': 'France',
  // Spain
  'Europe/Madrid': 'Spain',
  'Atlantic/Canary': 'Spain',
  'Africa/Ceuta': 'Spain',
  // Italy
  'Europe/Rome': 'Italy',
  // Netherlands
  'Europe/Amsterdam': 'Netherlands',
  // Sweden
  'Europe/Stockholm': 'Sweden',
  // Norway
  'Europe/Oslo': 'Norway',
  // Denmark
  'Europe/Copenhagen': 'Denmark',
  // Finland
  'Europe/Helsinki': 'Finland',
  'Europe/Mariehamn': 'Finland',
  // Switzerland
  'Europe/Zurich': 'Switzerland',
  // Ireland
  'Europe/Dublin': 'Ireland',
  // Portugal
  'Europe/Lisbon': 'Portugal',
  'Atlantic/Madeira': 'Portugal',
  'Atlantic/Azores': 'Portugal',
  // Belgium
  'Europe/Brussels': 'Belgium',
  // Austria
  'Europe/Vienna': 'Austria',
  // Poland
  'Europe/Warsaw': 'Poland',
  // Canada
  'America/Toronto': 'Canada',
  'America/Vancouver': 'Canada',
  'America/Edmonton': 'Canada',
  'America/Winnipeg': 'Canada',
  'America/Halifax': 'Canada',
  'America/St_Johns': 'Canada',
  'America/Regina': 'Canada',
  'America/Swift_Current': 'Canada',
  'America/Whitehorse': 'Canada',
  'America/Yellowknife': 'Canada',
  'America/Iqaluit': 'Canada',
  'America/Inuvik': 'Canada',
  'America/Cambridge_Bay': 'Canada',
  'America/Resolute': 'Canada',
  'America/Rankin_Inlet': 'Canada',
  'America/Atikokan': 'Canada',
  'America/Blanc-Sablon': 'Canada',
  'America/Goose_Bay': 'Canada',
  'America/Glace_Bay': 'Canada',
  'America/Moncton': 'Canada',
  'America/Nipigon': 'Canada',
  'America/Pangnirtung': 'Canada',
  'America/Rainy_River': 'Canada',
  'America/Thunder_Bay': 'Canada',
  'America/Dawson_Creek': 'Canada',
  'America/Creston': 'Canada',
  'America/Fort_Nelson': 'Canada',
  'America/Dawson': 'Canada',
  // Mexico
  'America/Mexico_City': 'Mexico',
  'America/Cancun': 'Mexico',
  'America/Monterrey': 'Mexico',
  'America/Mazatlan': 'Mexico',
  'America/Tijuana': 'Mexico',
  'America/Hermosillo': 'Mexico',
  'America/Chihuahua': 'Mexico',
  'America/Bahia_Banderas': 'Mexico',
  'America/Matamoros': 'Mexico',
  'America/Merida': 'Mexico',
  'America/Ojinaga': 'Mexico',
  // Brazil
  'America/Sao_Paulo': 'Brazil',
  'America/Rio_Branco': 'Brazil',
  'America/Manaus': 'Brazil',
  'America/Belem': 'Brazil',
  'America/Fortaleza': 'Brazil',
  'America/Recife': 'Brazil',
  'America/Bahia': 'Brazil',
  'America/Maceio': 'Brazil',
  'America/Noronha': 'Brazil',
  'America/Cuiaba': 'Brazil',
  'America/Eirunepe': 'Brazil',
  'America/Boa_Vista': 'Brazil',
  'America/Campo_Grande': 'Brazil',
  'America/Santarem': 'Brazil',
  'America/Araguaina': 'Brazil',
  'America/Porto_Velho': 'Brazil',
  // Chile
  'America/Santiago': 'Chile',
  'America/Punta_Arenas': 'Chile',
  'Pacific/Easter': 'Chile',
  // Colombia
  'America/Bogota': 'Colombia',
  // UAE
  'Asia/Dubai': 'United Arab Emirates',
  // Saudi Arabia
  'Asia/Riyadh': 'Saudi Arabia',
  // Qatar
  'Asia/Qatar': 'Qatar',
  // Israel
  'Asia/Jerusalem': 'Israel',
  'Asia/Tel_Aviv': 'Israel',
  // Turkey
  'Europe/Istanbul': 'Turkey',
  // Egypt
  'Africa/Cairo': 'Egypt',
  // South Africa
  'Africa/Johannesburg': 'South Africa',
  // Kenya
  'Africa/Nairobi': 'Kenya',
  // Nigeria
  'Africa/Lagos': 'Nigeria',
  // New Zealand
  'Pacific/Auckland': 'New Zealand',
  'Pacific/Chatham': 'New Zealand',
}

/**
 * ISO-3166 country code → country name. Used as a fallback when the IANA
 * timezone doesn't appear in TIMEZONE_TO_COUNTRY (e.g., uncommon zone or
 * the user's browser uses a private value). We pull the country region
 * from `navigator.language` (e.g., "en-US" → "US").
 *
 * Coverage matches COUNTRY_OPTIONS — anything else we leave blank.
 */
const LOCALE_REGION_TO_COUNTRY = {
  'TH': 'Thailand',
  'US': 'United States',
  'GB': 'United Kingdom',
  'SG': 'Singapore',
  'PH': 'Philippines',
  'IN': 'India',
  'AU': 'Australia',
  'ID': 'Indonesia',
  'VN': 'Vietnam',
  'MY': 'Malaysia',
  'JP': 'Japan',
  'KR': 'South Korea',
  'CN': 'China',
  'HK': 'Hong Kong',
  'TW': 'Taiwan',
  'DE': 'Germany',
  'FR': 'France',
  'ES': 'Spain',
  'IT': 'Italy',
  'NL': 'Netherlands',
  'SE': 'Sweden',
  'NO': 'Norway',
  'DK': 'Denmark',
  'FI': 'Finland',
  'CH': 'Switzerland',
  'IE': 'Ireland',
  'PT': 'Portugal',
  'BE': 'Belgium',
  'AT': 'Austria',
  'PL': 'Poland',
  'CA': 'Canada',
  'MX': 'Mexico',
  'BR': 'Brazil',
  'AR': 'Argentina',
  'CL': 'Chile',
  'CO': 'Colombia',
  'AE': 'United Arab Emirates',
  'SA': 'Saudi Arabia',
  'QA': 'Qatar',
  'IL': 'Israel',
  'TR': 'Turkey',
  'EG': 'Egypt',
  'ZA': 'South Africa',
  'KE': 'Kenya',
  'NG': 'Nigeria',
  'NZ': 'New Zealand',
}

// Map a handful of offsets to a recognizable city label. For any offset not
// listed, we just render "GMT±N". This keeps the dropdown compact (27 entries)
// without pulling in a full tz database.
const TIMEZONE_HINTS = {
  '-12': 'Baker Island',
  '-11': 'Pago Pago',
  '-10': 'Honolulu',
  '-9': 'Anchorage',
  '-8': 'Los Angeles',
  '-7': 'Denver',
  '-6': 'Mexico City',
  '-5': 'New York, Toronto',
  '-4': 'Santiago',
  '-3': 'São Paulo',
  '-2': 'South Georgia',
  '-1': 'Azores',
  '0': 'London, Lisbon',
  '1': 'Berlin',
  '2': 'Cairo',
  '3': 'Moscow',
  '4': 'Dubai',
  '5': 'Karachi',
  '6': 'Dhaka',
  '7': 'Bangkok, Jakarta',
  '8': 'Singapore',
  '9': 'Tokyo',
  '10': 'Sydney',
  '11': 'Nouméa',
  '12': 'Auckland',
  '13': 'Apia',
  '14': 'Kiritimati',
}

function buildTimezoneOptions() {
  const out = []
  for (let i = -12; i <= 14; i += 1) {
    const sign = i >= 0 ? '+' : '-'
    const abs = Math.abs(i)
    const city = TIMEZONE_HINTS[String(i)]
    const label = city
      ? `GMT${sign}${abs} (${city})`
      : `GMT${sign}${abs}`
    out.push({ value: i, label })
  }
  return out
}

// Cadence preset cards (Light / Balanced / Intense) are owned by the
// shared IntensityCadencePicker component — see
// src/components/intensity/IntensityCadencePicker.vue. The same picker
// is reused in Settings → Check-in Intensity, and its copy reflects
// the AQI feature's actual semantics (daily delivery, varying number
// of questions and open-ended share) rather than the older
// "X questions / week" framing.

// 24h HH:mm strings paired with friendly labels. Hourly from 6:00 AM
// through 5:00 PM, with extra :30 slots in the morning for finer
// control. Using HH:mm internally keeps the value timezone-agnostic and
// safe to round-trip through the persisted state.
const DELIVERY_TIME_OPTIONS = [
  { value: '06:00', label: '6:00 AM' },
  { value: '06:30', label: '6:30 AM' },
  { value: '07:00', label: '7:00 AM' },
  { value: '07:30', label: '7:30 AM' },
  { value: '08:00', label: '8:00 AM' },
  { value: '08:30', label: '8:30 AM' },
  { value: '09:00', label: '9:00 AM' },
  { value: '09:30', label: '9:30 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '13:00', label: '1:00 PM' },
  { value: '14:00', label: '2:00 PM' },
  { value: '15:00', label: '3:00 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '17:00', label: '5:00 PM' },
]

export default {
  name: 'WizardCompanyStep',
  components: { SignupStepFrame, SignupTextField, SignupSelect, IntensityCadencePicker },
  data() {
    return {
      form: {
        name: '',
        country: '',
        timezone: null,
        cadence: 'balanced',
        delivery_time: '08:00',
      },
      touched: {
        name: false,
        country: false,
        timezone: false,
        delivery_time: false,
      },
      // Tracks the *source* of the current timezone value. We override it
      // automatically when the user picks a country — but only if the
      // current value came from the browser default or a previous country
      // pick. Once the user picks a timezone manually, we lock in their
      // choice and stop auto-overriding.
      timezoneSource: 'unset',  // 'unset' | 'browser' | 'country' | 'user'
      countryOptions: COUNTRY_OPTIONS,
      timezoneOptions: buildTimezoneOptions(),
      deliveryTimeOptions: DELIVERY_TIME_OPTIONS,
      // Static HTML title — safe to v-html in SignupStepFrame because it's
      // a compile-time literal, not user input.
      titleHtml: 'Tell us about your <b>company</b>.',
    }
  },
  computed: {
    nameValid() {
      return String(this.form.name || '').trim().length > 0
    },
    countryValid() {
      return !!this.form.country
    },
    timezoneValid() {
      return typeof this.form.timezone === 'number'
    },
    cadenceValid() {
      return ['light', 'balanced', 'intense'].indexOf(this.form.cadence) !== -1
    },
    deliveryTimeValid() {
      return !!this.form.delivery_time
    },
    isValid() {
      return (
        this.nameValid &&
        this.countryValid &&
        this.timezoneValid &&
        this.cadenceValid &&
        this.deliveryTimeValid
      )
    },
    /**
     * Inline hint shown under the timezone select when the value was
     * inferred (browser or country). Falls silent once the user picks
     * manually so we don't keep nagging.
     */
    timezoneHint() {
      if (this.timezoneSource === 'browser') {
        return 'Detected from your browser — adjust if your HQ is elsewhere.'
      }
      if (this.timezoneSource === 'country') {
        return 'Defaulted from your country — adjust if your HQ is elsewhere.'
      }
      return ''
    },
  },
  created() {
    this.hydrateFromStore()
    // Browser-detect only when nothing was hydrated. A returning user
    // (sessionStorage) keeps whatever they chose last time.
    if (typeof this.form.timezone !== 'number') {
      const browserTz = this.detectBrowserTimezone()
      if (typeof browserTz === 'number') {
        this.form.timezone = browserTz
        this.timezoneSource = 'browser'
      }
    }
    if (!this.form.country) {
      const browserCountry = this.detectBrowserCountry()
      if (browserCountry) {
        this.form.country = browserCountry
        // Note: we do NOT trigger the country→timezone override here.
        // The browser's own offset is already the most accurate signal
        // for *this user* — overriding it with the country's primary
        // offset would be a regression for users sitting in a different
        // zone than the country's main hub.
      }
    }
  },
  methods: {
    trackIntensityTelemetry(payload = {}) {
      if (!this.$ga || typeof this.$ga.event !== 'function') return
      const {
        action = '',
        surface = 'wizard',
        control = '',
        cadence = '',
        fromCadence = '',
      } = payload
      this.$ga.event('intensity', action, `${surface}:${control}:${cadence}`, 0)
      this.$ga.event('intensity', 'cadence_picked', `${surface}:${fromCadence}->${cadence}`, 0)
      this.$ga.event('intensity', 'control_used', `${surface}:${control}`, 0)
    },
    cadenceSummaryFor(cadence) {
      const targets = CADENCE_TARGETS[cadence]
      if (!targets) return ''

      const questionsPerDay = targets.questions_per_day
        ? `${targets.questions_per_day.min}-${targets.questions_per_day.max}`
        : 'No cap'
      const openEndedPerWeek = targets.open_ended_per_week === null
        ? 'No cap'
        : `${targets.open_ended_per_week}`

      return `Questions/day: ${questionsPerDay} | Open-ended/week: ${openEndedPerWeek}`
    },
    hydrateFromStore() {
      const s = this.$store && this.$store.state && this.$store.state.signup
      if (!s || !s.wizardData || !s.wizardData.company) return
      const c = s.wizardData.company
      if (c.name) this.form.name = c.name
      if (c.country) this.form.country = c.country
      if (typeof c.timezone === 'number') {
        this.form.timezone = c.timezone
        // A hydrated value was previously confirmed by the user (they
        // navigated past Step 1 once). Treat as user-locked so we don't
        // clobber it on country edits.
        this.timezoneSource = 'user'
      }
      if (c.cadence) this.form.cadence = c.cadence
      if (c.delivery_time) this.form.delivery_time = c.delivery_time
    },
    /**
     * Returns the user's current UTC offset in whole hours, clamped to
     * the [-12, +14] range our select supports. Falls back to null if
     * the runtime doesn't expose a sensible offset.
     */
    detectBrowserTimezone() {
      try {
        const minutes = -new Date().getTimezoneOffset()
        if (!Number.isFinite(minutes)) return null
        const hours = Math.round(minutes / 60)
        return Math.max(-12, Math.min(14, hours))
      } catch (e) {
        return null
      }
    },
    /**
     * Best-effort country detection from the browser. Two-stage:
     *   1) IANA timezone (`Asia/Bangkok`) → country via TIMEZONE_TO_COUNTRY.
     *      Argentina's many sub-zones are pattern-matched.
     *   2) Locale region (`navigator.language` → "en-US" → "US") via
     *      LOCALE_REGION_TO_COUNTRY. Less reliable (a user with US English
     *      installed in Bangkok will still report en-US) but useful when
     *      the IANA zone is unrecognized.
     * Returns null if neither resolves to a country in our list.
     */
    detectBrowserCountry() {
      try {
        const tz = (Intl && Intl.DateTimeFormat && Intl.DateTimeFormat().resolvedOptions().timeZone) || ''
        if (tz) {
          if (tz.indexOf('America/Argentina/') === 0) return 'Argentina'
          const fromTz = TIMEZONE_TO_COUNTRY[tz]
          if (fromTz) return fromTz
        }
      } catch (e) {
        // Intl unavailable — fall through to locale fallback.
      }
      try {
        const lang =
          (navigator && navigator.language) ||
          (navigator && navigator.languages && navigator.languages[0]) ||
          ''
        const m = lang && lang.match(/-([A-Za-z]{2})$/)
        const region = m && m[1] && m[1].toUpperCase()
        if (region && LOCALE_REGION_TO_COUNTRY[region]) {
          return LOCALE_REGION_TO_COUNTRY[region]
        }
      } catch (e) {
        // navigator unavailable — give up.
      }
      return null
    },
    onCountryChange(value) {
      this.form.country = value
      this.touched.country = true
      // Only auto-fill the timezone if it hasn't been manually picked.
      // Browser-defaulted and previous-country-defaulted values are fair
      // game to overwrite — the country signal is more authoritative.
      if (this.timezoneSource !== 'user') {
        const tz = COUNTRY_TIMEZONE[value]
        if (typeof tz === 'number') {
          this.form.timezone = tz
          this.timezoneSource = 'country'
        }
      }
    },
    onTimezoneChange(value) {
      this.form.timezone = value
      this.touched.timezone = true
      this.timezoneSource = 'user'
    },
    onCadenceSelect(value) {
      const fromCadence = this.form.cadence
      this.trackIntensityTelemetry({
        action: 'wizard_intensity_changed',
        surface: 'wizard',
        control: 'card',
        cadence: value,
        fromCadence,
      })
      this.form.cadence = value
    },
    onNext() {
      // Mark everything touched so errors surface if the user tried to
      // force-submit somehow (e.g. Enter key).
      this.touched.name = true
      this.touched.country = true
      this.touched.timezone = true
      this.touched.delivery_time = true
      if (!this.isValid) return

      this.$store.commit('signup/UPDATE_COMPANY', {
        name: String(this.form.name || '').trim(),
        country: this.form.country,
        timezone: this.form.timezone,
        cadence: this.form.cadence,
        delivery_time: this.form.delivery_time,
      })
      this.$emit('next')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './_shared.scss';

.wiz-company {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.wiz-company__section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.wiz-company__section-title {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 800;
  color: var(--signup-text-soft);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
}

.wiz-company__required {
  color: var(--signup-brand);
  font-weight: 900;
}

.wiz-company__section-hint {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.45;
  color: var(--signup-text-muted);
  margin-top: -4px;
}

/* Country + Time zone live on one row at desktop, stack at narrow widths.
   Each child is a SignupSelect (block element with its own bottom margin),
   so we zero that here and rely on the row's gap. */
.wiz-company__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  ::v-deep .signup-sel { margin-bottom: 0; }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
}

/* Cadence preset cards live in src/components/intensity/IntensityCadencePicker.vue. */

/* =====================================================================
   Delivery time row — narrow select + inline help text on the right.
   ===================================================================== */
.wiz-company__time-field {
  display: flex;
  align-items: flex-end;
  gap: 12px;

  ::v-deep .signup-sel {
    flex: 0 0 auto;
    width: 220px;
    margin-bottom: 0;
  }
}

.wiz-company__time-help {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12px;
  color: var(--signup-text-muted);
  padding-bottom: 14px; // baseline-align with the 48px input
}
</style>
